import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { 
  FileText, 
  Clock, 
  CheckCircle2, 
  Coins, 
  Plus, 
  Loader2,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import Modal from "../../../components/admin/Modal";
import { useToast } from "../../../components/admin/ToastContext";
import { get, post } from "../../../services/lewooApi";
import { PageSkeleton } from "../../../components/admin/LoadingSkeleton";

function toNum(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

const STATE_MAP = {
  draft: "draft",
  submitted: "underReview",
  approved: "approved",
  paid: "reimbursed",
  refused: "refused",
};

const STATUS_STYLES = {
  draft: "bg-slate-50 text-slate-600 border-slate-100",
  underReview: "bg-amber-50 text-amber-700 border-amber-100",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-100",
  reimbursed: "bg-teal-50 text-teal-700 border-teal-100",
  refused: "bg-rose-50 text-rose-700 border-rose-100",
};

const STATUS_LABEL_IDS = {
  draft: "ess.expenses.statusDraft",
  underReview: "ess.expenses.statusSubmitted",
  approved: "ess.expenses.statusApproved",
  reimbursed: "ess.expenses.statusReimbursed",
  refused: "ess.expenses.statusRefused",
};

const TAB_LABEL_IDS = {
  All: "ess.expenses.tabAll",
  Draft: "ess.expenses.tabDraft",
  Submitted: "ess.expenses.tabSubmitted",
  Approved: "ess.expenses.tabApproved",
  Refused: "ess.expenses.tabRefused",
};

const ESSExpensesPage = () => {
  const intl = useIntl();
  const addToast = useToast();
  const { user } = useSelector((state) => state.auth);
  const [expenses, setExpenses] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({ description: "", amount: "" });
  const [submitting, setSubmitting] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;



  const loadData = useCallback(async () => {
    try {
      const emps = await get("/api/employees?limit=500");
      const allEmps = emps?.data || [];
      const currentEmp = allEmps.find((e) => e.work_email === user?.email) || null;
      const empId = currentEmp?.id || currentEmp?.employee_id;
      setEmployeeId(empId);

      const expRes = empId ? await get(`/api/expenses?employee_id=${empId}&limit=200`) : { data: [] };
      const expRecords = expRes?.data || [];

      setExpenses(expRecords.map(r => {
        const amountNum = toNum(r.amount);
        return {
          ref: `EXP-${r.id}`,
          description: r.name || intl.formatMessage({ id: "ess.expenses.noDescription" }),
          amount: amountNum.toLocaleString(intl.locale) + " XAF",
          amountNum,
          date: r.date ? new Date(r.date).toLocaleDateString(intl.locale) : "—",
          status: STATE_MAP[r.state] || "draft",
        };
      }));
    } catch (_) {
      setExpenses([]);
    }
    setLoaded(true);
  }, [intl]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    if (activeTab === "All") return expenses;
    return expenses.filter((e) => {
      if (activeTab === "Draft") return e.status === "draft";
      if (activeTab === "Submitted") return e.status === "underReview";
      if (activeTab === "Approved") return e.status === "approved";
      if (activeTab === "Refused") return e.status === "refused" || e.status === "reimbursed";
      return true;
    });
  }, [activeTab, expenses]);

  const tabCounts = useMemo(() => ({
    All: expenses.length,
    Draft: expenses.filter((e) => e.status === "draft").length,
    Submitted: expenses.filter((e) => e.status === "underReview").length,
    Approved: expenses.filter((e) => e.status === "approved").length,
    Refused: expenses.filter((e) => e.status === "refused").length,
  }), [expenses]);

  const paidTotal = expenses
    .filter(e => e.status === "reimbursed")
    .reduce((s, e) => s + e.amountNum, 0);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [activeTab]);

  const handleNewExpense = async () => {
    if (!form.description.trim() || !form.amount || !employeeId) {
      addToast(intl.formatMessage({ id: "ess.common.fillAllFields" }), "error");
      return;
    }
    setSubmitting(true);
    try {
      await post("/api/expenses", {
        name: form.description.trim(),
        amount: parseFloat(form.amount) || 0,
        employee_id: employeeId,
        date: new Date().toISOString().split("T")[0],
      });
      addToast(intl.formatMessage({ id: "ess.expenses.createSuccess" }), "success");
      setSubmitting(false);
      setModalOpen(false);
      setForm({ description: "", amount: "" });
      window.location.reload();
    } catch (_) {
      addToast(intl.formatMessage({ id: "ess.expenses.createError" }), "error");
      setSubmitting(false);
    }
  };

  if (!loaded) return <PageSkeleton />;

  const stats = [
    { labelId: "ess.expenses.draft", value: String(tabCounts.Draft), sub: intl.formatMessage({ id: "ess.expenses.subPending" }), icon: FileText, color: "text-slate-600 bg-slate-100" },
    { labelId: "ess.expenses.underReview", value: String(tabCounts.Submitted), sub: intl.formatMessage({ id: "ess.expenses.subInProgress" }), icon: Clock, color: "text-amber-600 bg-amber-100" },
    { labelId: "ess.expenses.approved", value: String(tabCounts.Approved), sub: intl.formatMessage({ id: "ess.expenses.subApproved" }), icon: CheckCircle2, color: "text-emerald-600 bg-emerald-100" },
    { labelId: "ess.expenses.reimbursed", value: paidTotal ? paidTotal.toLocaleString(intl.locale) + " XAF" : "0 XAF", sub: intl.formatMessage({ id: "ess.expenses.subReimbursed" }), icon: Coins, color: "text-teal-600 bg-teal-100" },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {intl.formatMessage({ id: "ess.expenses.title" })}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {intl.formatMessage({ id: "ess.expenses.subtitle" })}
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-all shadow-sm"
        >
          <Plus size={16} /> {intl.formatMessage({ id: "ess.expenses.newExpense" })}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ labelId, value, sub, icon: Icon, color }) => (
          <div key={labelId} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: labelId })}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                <p className="text-xs text-gray-400 mt-1">{sub}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon size={20} className={color.split(' ')[0]} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-1 px-4 pt-4 border-b border-gray-100 flex-wrap">
          {Object.entries(tabCounts).map(([label, count]) => (
            <button
              key={label}
              onClick={() => setActiveTab(label)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === label
                  ? "border-emerald-500 text-emerald-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {intl.formatMessage({ id: TAB_LABEL_IDS[label] })}
              <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{count}</span>
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-100">
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.expenses.colRef" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.expenses.colDescription" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.expenses.colAmount" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden sm:table-cell">{intl.formatMessage({ id: "ess.expenses.colDate" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.expenses.colStatus" })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginated.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-12 text-center text-gray-400">{intl.formatMessage({ id: "ess.expenses.empty" })}</td></tr>
              ) : (
                paginated.map((row) => (
                  <tr key={row.ref} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3.5 font-semibold text-gray-900">{row.ref}</td>
                    <td className="px-5 py-3.5 text-gray-700 max-w-[200px] truncate">{row.description}</td>
                    <td className="px-5 py-3.5 font-medium text-gray-900">{row.amount}</td>
                    <td className="px-5 py-3.5 text-gray-500 hidden sm:table-cell">{row.date}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES[row.status] || ""}`}>
                        {intl.formatMessage({ id: STATUS_LABEL_IDS[row.status] || "ess.expenses.statusDraft" })}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/30">
            <span className="text-xs text-gray-500">{intl.formatMessage({ id: "ess.common.pagination" }, { count: filtered.length, page, total: totalPages })}</span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50 transition-colors"
              ><ChevronLeft size={16} /></button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded border transition-colors ${
                    p === page ? "bg-emerald-500 text-white border-emerald-500" : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >{p}</button>
              ))}
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50 transition-colors"
              ><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={intl.formatMessage({ id: "ess.expenses.modalTitle" })}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.expenses.descLabel" })}</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              placeholder={intl.formatMessage({ id: "ess.expenses.descPlaceholder" })}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.expenses.amountLabel" })}</label>
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              placeholder={intl.formatMessage({ id: "ess.expenses.amountPlaceholder" })}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setModalOpen(false)} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">{intl.formatMessage({ id: "ess.common.cancel" })}</button>
            <button onClick={handleNewExpense} disabled={submitting} className="flex-1 px-4 py-2.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50">{submitting ? <><Loader2 size={16} className="animate-spin mr-2" /> {intl.formatMessage({ id: "ess.common.sending" })}</> : intl.formatMessage({ id: "ess.common.create" })}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ESSExpensesPage;
